import React, { useState, useEffect } from 'react'
import { List, Shuffle, Loader2 } from 'lucide-react'
import { apiClient } from '@/services/api'
import { getActivityTypeAccent, DIFFICULTY_COLORS } from '@/constants/activityTypes'

export interface QuestionBankItem {
  id: string
  activity_type: string
  category: string | null
  difficulty: string | null
  question: string
  hints: string[] | null
  explanation: string | null
  options: string[] | null
  correct_answer: any
  type_specific_data: Record<string, any>
}

interface QuestionBankPickerProps {
  activityType: string
  onSelect: (q: QuestionBankItem) => void
  onBulkAdd?: (questions: QuestionBankItem[]) => Promise<void>
  /** Extra column shown per row (e.g. test count, packet count) */
  renderExtra?: (q: QuestionBankItem) => React.ReactNode
}

export function QuestionBankPicker({
  activityType,
  onSelect,
  onBulkAdd,
  renderExtra,
}: QuestionBankPickerProps) {
  const [open, setOpen] = useState(false)
  const [questions, setQuestions] = useState<QuestionBankItem[]>([])
  const [loading, setLoading] = useState(false)
  const [catFilter, setCatFilter] = useState<Set<string>>(new Set())
  const [diffFilter, setDiffFilter] = useState<Set<string>>(new Set())
  const [randomCount, setRandomCount] = useState(5)
  const [bulkAdding, setBulkAdding] = useState(false)

  const accent = getActivityTypeAccent(activityType)

  // Derive categories and difficulties from loaded questions
  const categories = [...new Set(questions.map(q => q.category).filter(Boolean))] as string[]
  const difficulties = [...new Set(questions.map(q => q.difficulty).filter(Boolean))] as string[]

  // Reset when activity type changes
  useEffect(() => {
    setQuestions([])
    setCatFilter(new Set())
    setDiffFilter(new Set())
  }, [activityType])

  useEffect(() => {
    if (open && questions.length === 0) {
      setLoading(true)
      apiClient.getQuestionBankQuestions(activityType).then(res => {
        if (res.success && res.data) setQuestions(res.data)
      }).finally(() => setLoading(false))
    }
  }, [open, activityType, questions.length])

  const filtered = questions.filter(q => {
    if (catFilter.size > 0 && (!q.category || !catFilter.has(q.category))) return false
    if (diffFilter.size > 0 && (!q.difficulty || !diffFilter.has(q.difficulty))) return false
    return true
  })

  const handleRandomAdd = async () => {
    if (!onBulkAdd || filtered.length === 0) return
    const count = Math.min(randomCount, filtered.length)
    const shuffled = [...filtered]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    setBulkAdding(true)
    try { await onBulkAdd(shuffled.slice(0, count)) } finally { setBulkAdding(false) }
  }

  // Dynamically constructed Tailwind classes based on accent color
  const cls = {
    bg:           `bg-${accent}-500/10`,
    bgHover:      `hover:bg-${accent}-500/15`,
    border:       `border-${accent}-500/30`,
    text:         `text-${accent}-500`,
    bgActive:     `bg-${accent}-500/20`,
    borderActive: `border-${accent}-500/40`,
    bgSubtle:     `bg-${accent}-500/5`,
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full flex items-center gap-2 p-3 ${cls.bg} border ${cls.border} rounded-lg text-sm ${cls.text} ${cls.bgHover} transition-colors`}
      >
        <List className="w-4 h-4" />
        <span className="font-semibold">Load from Question Bank</span>
        <span className={`${cls.text} ml-auto`}>
          {questions.length > 0 ? `${questions.length} questions` : 'Browse'}
        </span>
      </button>
    )
  }

  return (
    <div className={`border ${cls.border} rounded-lg overflow-hidden`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-3 ${cls.bg}`}>
        <div className="flex items-center gap-2">
          <List className={`w-4 h-4 ${cls.text}`} />
          <span className={`text-sm font-semibold ${cls.text}`}>Question Bank</span>
          <span className={`text-xs ${cls.text}`}>
            {loading ? 'Loading...' : `(${filtered.length} shown)`}
          </span>
        </div>
        <button type="button" onClick={() => setOpen(false)} className={`text-xs ${cls.text} hover:opacity-75`}>
          Close
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className={`w-5 h-5 animate-spin ${cls.text}`} />
        </div>
      ) : (
        <>
          {/* Filters */}
          {(difficulties.length > 0 || categories.length > 0) && (
            <div className={`p-3 border-t ${cls.border} space-y-2`}>
              <div className="flex flex-wrap gap-1.5">
                {difficulties.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      const next = new Set(diffFilter)
                      next.has(d) ? next.delete(d) : next.add(d)
                      setDiffFilter(next)
                    }}
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                      diffFilter.has(d) ? (DIFFICULTY_COLORS[d] || `${cls.bgActive} ${cls.borderActive} ${cls.text}`) : 'bg-[var(--color-surface-deep)] border-[var(--color-border)] text-[var(--color-text-muted)]'
                    }`}
                  >
                    {d}
                  </button>
                ))}
                {difficulties.length > 0 && categories.length > 0 && (
                  <span className="mx-1 border-l border-[var(--color-border)]" />
                )}
                {categories.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      const next = new Set(catFilter)
                      next.has(c) ? next.delete(c) : next.add(c)
                      setCatFilter(next)
                    }}
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                      catFilter.has(c) ? `${cls.bgActive} ${cls.borderActive} ${cls.text}` : 'bg-[var(--color-surface-deep)] border-[var(--color-border)] text-[var(--color-text-muted)]'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Random selection */}
          {onBulkAdd && (
            <div className={`px-3 py-2 border-t ${cls.border} ${cls.bgSubtle} flex items-center gap-2`}>
              <Shuffle className={`w-4 h-4 ${cls.text}`} />
              <span className={`text-xs ${cls.text} font-medium`}>Add</span>
              <input
                type="number"
                min={1}
                max={filtered.length}
                value={randomCount}
                onChange={(e) => setRandomCount(Math.max(1, Math.min(filtered.length, parseInt(e.target.value) || 1)))}
                className={`w-12 px-1.5 py-0.5 text-xs text-center border ${cls.border} rounded bg-[var(--color-input-bg)] text-[var(--color-text)]`}
              />
              <span className={`text-xs ${cls.text} font-medium`}>random activities</span>
              <button
                type="button"
                onClick={handleRandomAdd}
                disabled={bulkAdding || filtered.length === 0}
                className="ds-btn ds-btn-primary ds-btn-sm ml-auto"
              >
                {bulkAdding ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Adding...</>
                ) : (
                  <><Shuffle className="w-3 h-3" /> Add Random</>
                )}
              </button>
            </div>
          )}

          {/* Question list */}
          <div className={`max-h-64 overflow-y-auto border-t ${cls.border}`}>
            {filtered.map(q => (
              <button
                key={q.id}
                type="button"
                onClick={() => { onSelect(q); setOpen(false) }}
                className={`w-full text-left px-3 py-2 border-b border-[var(--color-border)] ${cls.bgHover} transition-colors flex items-center gap-2`}
              >
                {q.difficulty && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${DIFFICULTY_COLORS[q.difficulty] || ''}`}>
                    {q.difficulty[0]}
                  </span>
                )}
                <span className="text-sm text-[var(--color-text)] font-medium truncate">{q.question}</span>
                {renderExtra ? (
                  <span className="text-[10px] text-[var(--color-muted)] ml-auto shrink-0">{renderExtra(q)}</span>
                ) : q.category ? (
                  <span className="text-[10px] text-[var(--color-muted)] ml-auto shrink-0">{q.category}</span>
                ) : null}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-[var(--color-muted)] text-center py-4">No questions match your filters</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
